import React from 'react';
import { arrayOf } from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  surveyReminderType,
  surveyScheduleType,
  langType
} from '../../../constants/types';
import { getTranslation, withLang } from '../../../i18n';
import { DISPLAY_DATE_FORMAT, LONG_DISPLAY_DATE_FORMAT } from '../../../constants';
import InfoBox from '../../InfoBox';
import InfoRow from '../../InfoBox/InfoRow';
import { isSurveyForDegreeProgram } from '../../../util';

const formatResponseCount = (responseCount, participantCount) => {
  const responsePercent = Math.floor((responseCount / participantCount) * 100);
  return Number.isInteger(responsePercent)
    ? `${responseCount}/${participantCount} (${responsePercent} %)`
    : `${responseCount}/${participantCount}`;
};

const StatisticsInfoBox = ({
  surveySchedule, surveyReminders, lang, translate: t
}) => {
  const surveyOptions = useSelector((state) => state.survey.surveyOptions);
  const ended = moment().isAfter(moment(surveySchedule.ended));
  const {
    title, code, type, degreeProgram,
    responseCount, participantCount, researchConsentGivenCount,
    reminderSentAt, statisticsComputedAt, feedbackViewedCount
  } = surveyOptions;

  const unsentReminders = surveyReminders
    .filter((surveyReminder) => !surveyReminder.sent)
    .map((surveyReminder) => (
      <li key={surveyReminder.id}>
        { moment(surveyReminder.scheduledTime).format(LONG_DISPLAY_DATE_FORMAT) }
      </li>
    ));

  const shouldShowReminderAndFeedbackCount = isSurveyForDegreeProgram(type) || type === 'HUT';

  return (
    <InfoBox>
      <>
        <InfoRow title={t('common.name')} content={getTranslation(title, lang)} />
        <InfoRow title={t('surveyParts.identifier')} content={code} />
        <InfoRow title={t('common.type')} content={t(`surveyType.${type}`)} />
        {isSurveyForDegreeProgram(type) && degreeProgram
          && <InfoRow title={t('common.degreeProgram')} content={getTranslation(degreeProgram.name, lang)} />}
        <InfoRow
          title={t(ended ? 'common.closed' : 'common.closing')}
          content={moment((ended && surveySchedule.ended) || surveySchedule.endDate).format(DISPLAY_DATE_FORMAT)}
        />
        <InfoRow
          title={t('surveyStatistics.responseCount')}
          content={formatResponseCount(responseCount, participantCount)}
        />
        {shouldShowReminderAndFeedbackCount && (
          <>
            <InfoRow
              title={t('surveyStatistics.researchConsentGivenCount')}
              content={`${researchConsentGivenCount}/${participantCount}`}
            />
            <InfoRow
              title={t('surveyStatistics.feedbackViewCount')}
              content={`${feedbackViewedCount}/${participantCount}`}
              tooltipContent={t('surveyStatistics.feedbackViewCountDisclaimer')}
            />
            <InfoRow
              title={t('surveyStatistics.reminderSentAt')}
              content={reminderSentAt ? moment(reminderSentAt).format(DISPLAY_DATE_FORMAT) : ''}
            />
          </>
        )}
        <InfoRow
          title={t('surveyStatistics.statisticsComputedAt')}
          content={statisticsComputedAt ? moment(statisticsComputedAt).format(LONG_DISPLAY_DATE_FORMAT) : ''}
        />
        {!ended && shouldShowReminderAndFeedbackCount && (
          <InfoRow
            title={t('surveyStatistics.reminderTimes')}
            content={<ul>{unsentReminders}</ul>}
          />
        )}
      </>
    </InfoBox>
  );
};

StatisticsInfoBox.propTypes = {
  surveySchedule: surveyScheduleType,
  surveyReminders: arrayOf(surveyReminderType),
  ...langType
};

StatisticsInfoBox.defaultProps = {
  surveySchedule: null,
  surveyReminders: []
};

export default withLang(StatisticsInfoBox);
